import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PersonaChooser from "@/pages/PersonaChooser";
import Dashboard from "@/pages/Dashboard";
import PropertyIC from "@/pages/PropertyIC";
import PropertyDetail from "@/pages/PropertyDetail";
import PropertyCompare from "@/pages/PropertyCompare";
import Admin from "@/pages/Admin";
import MisDashboard from "@/pages/mis/MisDashboard";
import MisCategoryExpenses from "@/pages/mis/MisCategoryExpenses";
import MisExpenses from "@/pages/mis/MisExpenses";
import MisOutliers from "@/pages/mis/MisOutliers";

function ProtectedRoute({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-paper">
        <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-zinc-500">Verifying session…</div>
      </div>
    );
  }
  if (!user && !location.state?.user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of session_id in URL fragment to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/persona" element={<ProtectedRoute><PersonaChooser /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/properties" element={<Navigate to="/property-ic/drafts" replace />} />
      <Route path="/property-ic" element={<Navigate to="/property-ic/drafts" replace />} />
      <Route path="/property-ic/drafts" element={<ProtectedRoute><PropertyIC mode="drafts" /></ProtectedRoute>} />
      <Route path="/property-ic/submission/new" element={<ProtectedRoute><PropertyIC mode="submission" /></ProtectedRoute>} />
      <Route path="/property-ic/submission/:id" element={<ProtectedRoute><PropertyIC mode="submission" /></ProtectedRoute>} />
      <Route path="/operational/:location" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><PropertyCompare /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      {/* MIS Analysis persona */}
      <Route path="/mis/dashboard" element={<ProtectedRoute><MisDashboard /></ProtectedRoute>} />
      <Route path="/mis/expenses" element={<ProtectedRoute><MisCategoryExpenses /></ProtectedRoute>} />
      <Route path="/mis/property-tracker" element={<ProtectedRoute><MisExpenses /></ProtectedRoute>} />
      <Route path="/mis/outliers" element={<ProtectedRoute><MisOutliers /></ProtectedRoute>} />
      <Route path="/mis/compare" element={<ProtectedRoute><PropertyCompare /></ProtectedRoute>} />
      <Route path="/mis/property/:location" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
      <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
      <Route path="/intelligence" element={<Navigate to="/property-ic/drafts" replace />} />
      <Route path="/mis-upload" element={<Navigate to="/admin" replace />} />
      <Route path="/" element={<Navigate to="/persona" replace />} />
      <Route path="*" element={<Navigate to="/persona" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
